import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, FileText, CheckCircle2, XCircle, Loader2, Send, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TEMPLATES = [
  {
    id: "magic_link",
    label: "Candidate Magic Link",
    desc: "15-minute login link that Securado candidates use to access their portal.",
    icon: Link2,
  },
  {
    id: "test_invite",
    label: "Test Invitation",
    desc: "7-day magic-link invitation to complete a screening test for a specific job.",
    icon: FileText,
    needsJob: true,
  },
  {
    id: "shortlist",
    label: "Shortlist Notification",
    desc: "Congratulatory email fired after an admin approves an AI shortlist recommendation.",
    icon: CheckCircle2,
    needsJob: true,
  },
  {
    id: "rejection",
    label: "Rejection Email",
    desc: "Polite rejection fired after an admin rejects a candidate.",
    icon: XCircle,
    needsJob: true,
  },
  {
    id: "panel_invitation",
    label: "Panel Invitation",
    desc: "Sent when an interviewer is added to a job — contains their personal panel-dashboard magic link.",
    icon: Users,
    needsJob: true,
  },
];

/**
 * Admin-only QA panel for firing each transactional email template
 * against an arbitrary address. Avoids the need to create fake candidates.
 */
export default function EmailDiagnosticsCard({ enabled }) {
  const [toEmail, setToEmail] = useState("");
  const [candidateName, setCandidateName] = useState("QA Candidate");
  const [jobTitle, setJobTitle] = useState("Sample Job");
  const [lastResults, setLastResults] = useState({});
  const [sending, setSending] = useState(null);

  const fire = async (template) => {
    if (!toEmail) {
      toast.error("Enter a recipient email first");
      return;
    }
    setSending(template);
    const startedAt = new Date();
    try {
      const { data } = await api.post("/admin/settings/email/diagnose", {
        template,
        to_email: toEmail,
        candidate_name: candidateName,
        job_title: jobTitle,
      });
      setLastResults((prev) => ({
        ...prev,
        [template]: { ok: true, message: data.message, at: startedAt.toLocaleTimeString() },
      }));
      toast.success(data.message);
    } catch (error) {
      const detail = error.response?.data?.detail || "Request failed";
      setLastResults((prev) => ({
        ...prev,
        [template]: { ok: false, message: detail, at: startedAt.toLocaleTimeString() },
      }));
      toast.error(detail);
    } finally {
      setSending(null);
    }
  };

  return (
    <Card data-testid="email-diagnostics-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Email Diagnostics</CardTitle>
            <CardDescription>
              Fire any transactional template against a target address. Useful for verifying O365 delivery end-to-end without creating test candidates.
            </CardDescription>
          </div>
          <Badge variant={enabled ? "default" : "outline"} data-testid="diagnostics-enabled-badge">
            {enabled ? "SMTP enabled" : "SMTP disabled"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!enabled && (
          <div className="p-3 rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-800" data-testid="diagnostics-disabled-warning">
            Email service is currently <strong>disabled</strong>. Templates below will call the backend but the SMTP layer will skip the actual send. Toggle <em>Enable Email Service</em> on the Email tab and save before verifying delivery.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Label htmlFor="diag-to-email">Recipient email *</Label>
            <Input
              id="diag-to-email"
              type="email"
              placeholder="you@securado.net"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              data-testid="diagnostics-to-email-input"
            />
          </div>
          <div className="md:col-span-1">
            <Label htmlFor="diag-cand-name">Sample candidate name</Label>
            <Input
              id="diag-cand-name"
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              data-testid="diagnostics-candidate-name-input"
            />
          </div>
          <div className="md:col-span-1">
            <Label htmlFor="diag-job-title">Sample job title</Label>
            <Input
              id="diag-job-title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              data-testid="diagnostics-job-title-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            const result = lastResults[t.id];
            const isSending = sending === t.id;
            return (
              <div
                key={t.id}
                className="border rounded-lg p-4 bg-white flex flex-col gap-3"
                data-testid={`diagnostics-template-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{t.label}</div>
                    <p className="text-xs text-gray-600 leading-snug mt-0.5">{t.desc}</p>
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fire(t.id)}
                  disabled={isSending || !toEmail}
                  className="self-start"
                  data-testid={`diagnostics-fire-${t.id}`}
                >
                  {isSending ? (
                    <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-2" />
                  )}
                  Send {t.label}
                </Button>

                {result && (
                  <div
                    className={`text-xs rounded border px-2.5 py-2 ${
                      result.ok
                        ? "border-green-200 bg-green-50 text-green-800"
                        : "border-red-200 bg-red-50 text-red-800"
                    }`}
                    data-testid={`diagnostics-result-${t.id}`}
                  >
                    <div className="flex items-start gap-1.5">
                      {result.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> : <XCircle className="w-3.5 h-3.5 mt-0.5" />}
                      <div>
                        <div className="font-medium">{result.ok ? "Delivered to SMTP" : "Send failed"}</div>
                        <div className="opacity-80">{result.message}</div>
                        <div className="opacity-60 mt-0.5">at {result.at}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
